import React, { useEffect, useState } from 'react';
import {
  Col,
  Row,
  Spinner,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
} from 'reactstrap';
import './ProfileStyles.scss';
import {
  getStoreVisits,
  getVisitedStores,
} from '../../../services/StoreVisitService';

const StoreVisits = ({ member }) => {
  const { _id: memberId } = member;
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visitedStores, setVisitedStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');

  useEffect(() => {
    if (!memberId) return;
    getVisitedStores(memberId).then((data) => setVisitedStores(data || []));
  }, [memberId]);

  useEffect(() => {
    if (!memberId) return;
    setLoading(true);
    getStoreVisits(memberId, false, selectedStore)
      .then((data) => {
        setVisits(data || []);
      })
      .finally(() => setLoading(false));
  }, [memberId, selectedStore]);

  // Transform API response to group by year/month and then by date
  const grouped = visits.reduce((acc, period) => {
    const { year, month, transactionsByDate } = period;
    const monthName = new Date(year, month - 1).toLocaleString('default', {
      month: 'short',
    });
    const periodKey = `${monthName} ${year}`;
    if (!acc[periodKey]) acc[periodKey] = [];
    Object.entries(transactionsByDate).forEach(([date, txns]) => {
      txns.forEach((txn) => {
        acc[periodKey].push({ ...txn, _date: date });
      });
    });
    return acc;
  }, {});

  // Sort periods descending (latest first)
  const sortedPeriods = Object.keys(grouped).sort((a, b) => {
    const [ma, ya] = a.split(' ');
    const [mb, yb] = b.split(' ');
    const da = new Date(`${ma} 1, ${ya}`);
    const db = new Date(`${mb} 1, ${yb}`);
    return db - da;
  });

  // Helper to sort by date descending
  const sortByDateDesc = (a, b) => new Date(b._date) - new Date(a._date);

  // Ensure visitedStores is always an array
  const safeVisitedStores = Array.isArray(visitedStores) ? visitedStores : [];

  // Store visit counts for status cards
  const storeVisitCounts = safeVisitedStores.map((store) => {
    const count = visits.reduce((acc, period) => {
      const { transactionsByDate } = period;
      return (
        acc +
        Object.values(transactionsByDate)
          .flat()
          .filter((v) => v.storeId === store.storeId).length
      );
    }, 0);
    return { ...store, count };
  });

  // Helper to display brand and store
  const getStoreDisplay = (store) => {
    const brand = store.brand || store.storeName || '';
    const storeId = store.storeId || '';
    return brand && storeId ? `${brand} (${storeId})` : brand || storeId;
  };

  return (
    <>
      <Row className="mb-3">
        <Col md={8} className="d-flex gap-2 align-items-end flex-wrap">
          {storeVisitCounts.map((store) => (
            <Card
              key={store.storeId}
              className="mb-2"
              style={{ minWidth: 180 }}
            >
              <CardBody className="py-2 px-3">
                <div className="fw-bold">{getStoreDisplay(store)}</div>
                <div className="text-muted fs-12">Visits: {store.count}</div>
              </CardBody>
            </Card>
          ))}
        </Col>
        <Col md={4} className="d-flex align-items-end justify-content-end">
          <FormGroup className="w-100 mb-0">
            <Label for="storeFilter">Filter by Store</Label>
            <Input
              type="select"
              id="storeFilter"
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
            >
              <option value="">All Stores</option>
              {safeVisitedStores.map((store) => (
                <option key={store.storeId} value={store.storeId}>
                  {getStoreDisplay(store)}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <div className="timeline-2">
            {loading ? (
              <div className="text-center my-4">
                <Spinner /> Loading store visits...
              </div>
            ) : visits.length === 0 ? (
              <div className="text-center my-4 text-muted">
                No store visits found.
              </div>
            ) : (
              sortedPeriods.map((period) => (
                <React.Fragment key={period}>
                  <div className="timeline-year">
                    <p className="shadow bg-light">{period}</p>
                  </div>
                  <div className="timeline-continue">
                    {grouped[period].sort(sortByDateDesc).map((visit) => (
                      <Row className="timeline-right" key={visit._id}>
                        <Col xs={12}>
                          <p className="timeline-date-custom">
                            {new Date(visit._date).toLocaleDateString(
                              undefined,
                              { month: 'short', day: '2-digit' }
                            )}
                          </p>
                        </Col>
                        <Col xs={12}>
                          <div className="d-flex timeline-box-custom align-items-start gap-1">
                            <span
                              className="bg-light rounded-circle p-1 d-flex align-items-center"
                              style={{ width: 20, height: 20 }}
                            >
                              <i className="bx bx-cart"></i>
                            </span>
                            <div className="timeline-text">
                              <h6 className="my-1 text-body">Store Visit</h6>
                              <p className="mb-1 fs-12 text-body-secondary">
                                {visit.brand} - {visit.storeId} (
                                {visit.countryCode})<br />
                                Items: {visit.totalQty} | Total:{' '}
                                {visit.totalPrice}
                              </p>
                              <div className="d-flex gap-2">
                                <span className="badge rounded-pill bg-light fw-normal fs-10 text-muted d-block">
                                  {new Date(visit.createdAt).toLocaleTimeString(
                                    [],
                                    { hour: '2-digit', minute: '2-digit' }
                                  )}
                                </span>
                                <span className="badge rounded-pill bg-light fw-normal fs-10 text-muted d-block">
                                  {visit.stationId}
                                </span>
                              </div>
                              {visit.requestItems &&
                                visit.requestItems.length > 0 && (
                                  <div className="mt-2">
                                    <strong>Items:</strong>
                                    <ul className="mb-0 ps-3">
                                      {visit.requestItems.map((item, idx) => (
                                        <li key={item.id || idx}>
                                          {item.brand} - {item.categoryLevel1}
                                          {item.categoryLevel2
                                            ? ` / ${item.categoryLevel2}`
                                            : ''}
                                          {item.categoryLevel3
                                            ? ` / ${item.categoryLevel3}`
                                            : ''}{' '}
                                          | Qty: {item._qty} | Price: {item._sp}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    ))}
                  </div>
                </React.Fragment>
              ))
            )}
            <div className="timeline-launch">
              <div className="timeline-box shadow">
                <div className="timeline-text">
                  <h5>Customer Activity</h5>
                  <p className="text-muted text-capitalize mb-0">
                    Wow...!!! What a Journey So Far...!!!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default StoreVisits;
